import type { LassoMode } from "./lasso.ts";

export interface CircleSelection {
  points: number[];
  lines: number[];
}

/** Indexes already painted in this stroke, so later samples skip them. */
export interface CircleStroke {
  pointMark: Uint8Array;
  lineMark: Uint8Array;
  /** Replace clears once, then the rest of the stroke adds. */
  replaced: boolean;
}

/**
 * Apply the points and lines a circle sample just touched.
 * Replace starts from those hits. Add unions them. Subtract removes the
 * points, the lines, and both ends of each line so a touched line turns off.
 * `dropLineEnds` stays on for a mixed selection. Lines-only leaves the points.
 * Returns null when this sample touches nothing new.
 */
export function applyCirclePaint(
  current: CircleSelection,
  hitPoints: readonly number[],
  hitLines: readonly number[],
  mode: LassoMode,
  stroke: CircleStroke,
  segments: ReadonlyArray<readonly [number, number]>,
  dropLineEnds = true,
): CircleSelection | null {
  const points = freshIndexes(hitPoints, stroke.pointMark);
  const lines = freshIndexes(hitLines, stroke.lineMark);
  if (mode === "replace" && !stroke.replaced) {
    stroke.replaced = true;
    if (points.length === 0 && lines.length === 0) {
      if (current.points.length === 0 && current.lines.length === 0) return null;
      return { points: [], lines: [] };
    }
    return { points: uniqueSorted(points), lines: uniqueSorted(lines) };
  }
  if (points.length === 0 && lines.length === 0) return null;
  if (mode === "subtract") {
    const dropPoints = new Set(points);
    if (dropLineEnds) {
      for (const line of lines) {
        const segment = segments[line];
        if (!segment) continue;
        dropPoints.add(segment[0]);
        dropPoints.add(segment[1]);
        markIndex(stroke.pointMark, segment[0]);
        markIndex(stroke.pointMark, segment[1]);
      }
    }
    const dropLines = new Set(lines);
    const nextPoints = current.points.filter((index) => !dropPoints.has(index));
    const nextLines = current.lines.filter((index) => !dropLines.has(index));
    if (nextPoints.length === current.points.length && nextLines.length === current.lines.length) return null;
    return { points: nextPoints, lines: nextLines };
  }
  return {
    points: uniqueSorted([...current.points, ...points]),
    lines: uniqueSorted([...current.lines, ...lines]),
  };
}

function freshIndexes(hits: readonly number[], mark: Uint8Array): number[] {
  const fresh: number[] = [];
  for (let i = 0; i < hits.length; i++) {
    const index = hits[i];
    if (index == null || index < 0 || index >= mark.length || mark[index]) continue;
    mark[index] = 1;
    fresh.push(index);
  }
  return fresh;
}

function markIndex(mark: Uint8Array, index: number): void {
  if (index >= 0 && index < mark.length) mark[index] = 1;
}

function uniqueSorted(indices: readonly number[]): number[] {
  return [...new Set(indices)].sort((a, b) => a - b);
}
